import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useGeolocation } from '@/hooks/useGeolocation';
import { Position } from '@certhon/models';
import { AlertCircle, MapPin, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { LocationMapBox } from '@/features/tickets/components/LocationMapBox';

interface GeolocationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (location: { position: Position; label: string }) => void;
  disabled?: boolean;
}

export function GeolocationModal({
  open,
  onOpenChange,
  onSubmit,
  disabled = false,
}: GeolocationModalProps) {
  const [locationLabel, setLocationLabel] = useState('');
  const {
    position,
    error,
    loading,
    getCurrentPosition,
    clearError,
    reset,
    isSupported,
  } = useGeolocation({
    enableHighAccuracy: true,
    timeout: 15000,
    maximumAge: 60000, // 1 minute
  });

  const handleGetLocation = () => {
    clearError();
    getCurrentPosition();
  };

  const handleSubmit = () => {
    if (position && locationLabel.trim()) {
      onSubmit({
        position,
        label: locationLabel.trim(),
      });
      handleClose();
    }
  };

  const handleClose = () => {
    setLocationLabel('');
    reset();
    onOpenChange(false);
  };

  if (!isSupported) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="text-destructive h-5 w-5" />
              Location Not Supported
            </DialogTitle>
            <DialogDescription>
              Your browser does not support geolocation services. Please use a
              modern browser with location access enabled.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleClose}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Add Location
          </DialogTitle>
          <DialogDescription>
            Add your current location. Click &quot;Get Location&quot; to
            retrieve your coordinates.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Location acquisition section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Current Location</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleGetLocation}
                disabled={loading || disabled}
                className="h-8"
              >
                {loading ? (
                  <RefreshCw className="mr-1 h-3 w-3 animate-spin" />
                ) : (
                  <MapPin className="mr-1 h-3 w-3" />
                )}
                {loading ? 'Getting Location...' : 'Get Location'}
              </Button>
            </div>

            {/* Location preview */}
            {position && (
              <LocationMapBox
                position={position}
                label="Current Location"
                showMapProviders={false}
                minHeight="min-h-[150px]"
              />
            )}

            {/* Error display */}
            {error && (
              <div className="bg-destructive/10 border-destructive/20 rounded-lg border p-3">
                <div className="flex items-start gap-2">
                  <AlertCircle className="text-destructive mt-0.5 h-4 w-4 flex-shrink-0" />
                  <div className="text-destructive text-sm">{error}</div>
                </div>
              </div>
            )}
          </div>

          {/* Location description input */}
          <div className="space-y-2">
            <Label htmlFor="location-label">Location Description</Label>
            <Input
              id="location-label"
              placeholder="e.g., Office parking, Customer site, Building entrance..."
              value={locationLabel}
              onChange={(e) => setLocationLabel(e.target.value)}
              disabled={disabled}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={disabled}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!position || !locationLabel.trim() || disabled || loading}
          >
            Add Location
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
